import { useCallback, useRef, useState, useEffect } from "react";

import { Box, Stack } from "@mui/material";

import { TextFields } from "@mui/icons-material";
import { doc, updateDoc } from "firebase/firestore";
import debounce from "lodash/debounce";
import { MenuButton, type RichTextEditorRef } from "mui-tiptap";

import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { UserRole } from "@/types";
import { db } from "@/utils/firebase";

import EditorMenuControls from "./EditorMenuControls";
import { StyledRichTextEditor } from "./styles";
import useExtensions from "./useExtensions";

interface Note {
  content: string;
  lastModified: Date;
}
interface LessonNotesProps {
  readOnly?: boolean;
  isVisible: boolean;
}

const LessonNotes = ({ readOnly = false, isVisible }: LessonNotesProps) => {
  const { userRole } = useFirebaseContext();
  const { lesson, lessonInteractionProfile } = useLesson();
  const [note, setNote] = useState<Note>(() => {
    try {
      const storageKey = lesson?.id ? `lessonNote_${lesson.id}` : "";
      const savedNote = localStorage.getItem(storageKey);
      if (savedNote) {
        const parsedNote = JSON.parse(savedNote);
        return {
          content: parsedNote.content || "",
          lastModified: new Date(parsedNote.lastModified),
        };
      }
    } catch (error) {
      console.error("Error loading note from localStorage:", error);
    }
    return {
      content: "",
      lastModified: new Date(),
    };
  });

  const debouncedSave = useCallback(
    debounce(async (content: string) => {
      if (!lessonInteractionProfile?.id) return;

      try {
        const interactionProfileRef = doc(
          db,
          "lessonInteractionProfiles",
          lessonInteractionProfile?.id
        );
        const fieldName =
          userRole === UserRole.Tutor ? "tutorNotes" : "studentNotes";
        await updateDoc(interactionProfileRef, {
          [fieldName]: content,
        });
      } catch (error) {
        console.error("Error saving note to database:", error);
      }
    }, 2000),
    [lesson?.id, userRole]
  );

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  const handleUpdate = (editor: any) => {
    const content = editor.getHTML();
    if (content !== note.content) {
      const updatedNote = {
        content,
        lastModified: new Date(),
      };
      setNote(updatedNote);
      const storageKey = lesson?.id ? `lessonNote_${lesson.id}` : "lessonNote";
      localStorage.setItem(storageKey, JSON.stringify(updatedNote));
      debouncedSave(content);
    }
  };

  const extensions = useExtensions({
    placeholder: "Start taking notes...",
  });
  const rteRef = useRef<RichTextEditorRef>(null);
  const [showMenuBar, setShowMenuBar] = useState(true);

  return (
    <Box
      sx={{
        height: "100%",
        p: 2,
        flexDirection: "column",
        display: isVisible ? "flex" : "none",
      }}
    >
      <StyledRichTextEditor
        ref={rteRef}
        extensions={extensions}
        content={note.content}
        onUpdate={({ editor }) => handleUpdate(editor)}
        editable={!readOnly}
        renderControls={() => (!readOnly ? <EditorMenuControls /> : null)}
        RichTextFieldProps={{
          variant: "outlined",
          MenuBarProps: {
            hide: !showMenuBar || readOnly,
          },
          footer: !readOnly ? (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                borderTopStyle: "solid",
                borderTopWidth: 1,
                borderTopColor: (theme) => theme.palette.divider,
                py: 1,
                px: 1.5,
              }}
            >
              <MenuButton
                value="formatting"
                tooltipLabel={
                  showMenuBar ? "Hide formatting" : "Show formatting"
                }
                size="small"
                onClick={() => setShowMenuBar((currentState) => !currentState)}
                selected={showMenuBar}
                IconComponent={TextFields}
              />
            </Stack>
          ) : null,
        }}
      />
    </Box>
  );
};

export default LessonNotes;
